/**
 * Control Presupuestario → P&L: el dashboard completo. Lee `financePnl.getDashboard`
 * (snapshot de BigQuery, 6 h) y arma los siete bloques con `shared/finance-pnl.ts`:
 * KPIs, waterfall, drivers + FX, COGS por Solución, Investment por business case,
 * tabla y heatmap. Controles: mes de referencia (último cerrado por defecto),
 * período (mes / YTD), contra qué comparar (BGT / mes anterior), notas y
 * "Actualizar" (relee BigQuery).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { computeKpis, computeWaterfall, hasBudget, type PnlBase, type PnlPeriod } from '../../../../../shared/finance-pnl';
import { usePnlFmt } from './pnl-format';
import { PnlMonthSelect, Seg, SegLabel } from './pnl-ui';
import { PnlKpis } from './PnlKpis';
import { PnlWaterfall, type WaterfallKey } from './PnlWaterfall';
import { PnlDrivers } from './PnlDrivers';
import { PnlBreakdown } from './PnlBreakdown';
import { PnlTable } from './PnlTable';
import { PnlHeatmap } from './PnlHeatmap';

const YEAR = 2026;

export function PnlDashboard() {
  const { t } = useTranslation();
  const fmt = usePnlFmt(YEAR);
  const utils = trpc.useUtils();
  const query = trpc.financePnl.getDashboard.useQuery({ year: YEAR }, { staleTime: 5 * 60 * 1000, retry: false });
  const [refreshing, setRefreshing] = useState(false);
  const [monthSel, setMonthSel] = useState<number | null>(null);
  const [period, setPeriod] = useState<PnlPeriod>('month');
  const [baseSel, setBaseSel] = useState<PnlBase>('budget');
  const [notes, setNotes] = useState(false);
  const [selected, setSelected] = useState<WaterfallKey>('revenue');

  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await utils.financePnl.getDashboard.fetch({ year: YEAR, refresh: true });
      utils.financePnl.getDashboard.setData({ year: YEAR }, fresh);
      toast.success(t('budget_control.pnl.refreshed'));
    } catch (e) {
      toast.error(t('budget_control.pnl.refresh_failed', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setRefreshing(false);
    }
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <WinclapLoader size={40} />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('budget_control.pnl.load_error_title')}</AlertTitle>
        <AlertDescription>
          {t('budget_control.pnl.load_error_desc')} {query.error?.message}
        </AlertDescription>
      </Alert>
    );
  }
  const data = query.data;
  if (data.closedMonth < 1) {
    return (
      <Alert>
        <AlertTitle>{t('budget_control.pnl.no_data_title')}</AlertTitle>
        <AlertDescription>{t('budget_control.pnl.no_data_desc', { year: data.year })}</AlertDescription>
      </Alert>
    );
  }

  const month = monthSel !== null && monthSel >= 1 && monthSel <= data.closedMonth ? monthSel : data.closedMonth;
  const withBudget = hasBudget(data);
  const prevAllowed = period === 'month' && month > 1;
  const base: PnlBase = !withBudget ? 'prev' : prevAllowed && baseSel === 'prev' ? 'prev' : 'budget';
  const periodLabel = period === 'month' ? fmt.monthLong(month) : `${fmt.monthShort(1)}–${fmt.monthShort(month)}`;
  const kpis = computeKpis(data, month, period);
  const waterfall = computeWaterfall(data, month, period);
  const months = Array.from({ length: data.closedMonth }, (_, i) => i + 1);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-accent/60 px-4 py-3 text-xs text-muted-foreground">
        <p className="max-w-[90ch]">
          <strong className="text-foreground">{t('budget_control.pnl.banner_lead')}</strong> {t('budget_control.pnl.banner_text')}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <SegLabel>{t('budget_control.pnl.month')}</SegLabel>
        <PnlMonthSelect value={month} onChange={setMonthSel} months={months} year={data.year} fmt={fmt} ariaLabel={t('budget_control.pnl.month')} />
        <SegLabel>{t('budget_control.pnl.period')}</SegLabel>
        <Seg<PnlPeriod>
          ariaLabel={t('budget_control.pnl.period')}
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'month', label: fmt.monthLong(month) },
            { value: 'ytd', label: t('budget_control.pnl.ytd', { from: fmt.monthShort(1), to: fmt.monthShort(month) }) },
          ]}
        />
        {withBudget && (
          <>
            <SegLabel>{t('budget_control.pnl.compare')}</SegLabel>
            <Seg<PnlBase>
              ariaLabel={t('budget_control.pnl.compare')}
              value={base}
              onChange={setBaseSel}
              options={[
                { value: 'budget', label: t('budget_control.pnl.budget') },
                { value: 'prev', label: month > 1 ? fmt.monthLong(month - 1) : t('budget_control.pnl.prev_month'), disabled: !prevAllowed },
              ]}
            />
          </>
        )}
        <span className="flex-1" />
        <button
          type="button"
          aria-pressed={notes}
          onClick={() => setNotes((v) => !v)}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground',
            notes && 'border-primary bg-primary text-primary-foreground'
          )}
        >
          <span className={cn('relative h-3 w-[22px] rounded-full bg-border transition-colors', notes && 'bg-white/45')}>
            <span className={cn('absolute left-0.5 top-0.5 h-2 w-2 rounded-full bg-card transition-transform', notes && 'translate-x-2.5 bg-white')} />
          </span>
          {t('budget_control.pnl.notes')}
        </button>
        <Button size="sm" variant="outline" className="gap-1.5" onClick={refresh} disabled={refreshing}>
          {refreshing ? <WinclapLoader size={14} /> : <RefreshCw className="h-3.5 w-3.5" />}
          {t('budget_control.pnl.refresh')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
        <span>
          <b className="font-semibold text-foreground">{t('budget_control.pnl.closed_month', { month: fmt.monthLong(data.closedMonth), year: data.year })}</b>
          {' · '}
          {t('budget_control.pnl.data_as_of', { at: fmt.dateTime(data.generatedAt) })}
        </span>
        {!withBudget && <span style={{ color: 'var(--pnl-warn)' }}>{t('budget_control.pnl.no_budget', { year: data.year })}</span>}
        <details className="inline-block">
          <summary className="cursor-pointer select-none">{t('budget_control.pnl.sources_title')}</summary>
          <ul className="mt-1 list-disc pl-5 font-mono">
            {data.sources.map((s) => (
              <li key={s.table}>
                {s.table}
                {' · '}
                {s.lastModified ? t('budget_control.pnl.source_updated', { at: fmt.dateTime(s.lastModified) }) : t('budget_control.pnl.source_unknown')}
              </li>
            ))}
          </ul>
        </details>
      </div>

      <PnlKpis kpis={kpis} base={base} period={period} month={month} closedMonth={data.closedMonth} fmt={fmt} notes={notes} />
      {withBudget && (
        <PnlWaterfall steps={waterfall} selected={selected} onSelect={setSelected} periodLabel={periodLabel} year={data.year} splitMonth={data.planSplitMonth} fmt={fmt} notes={notes} />
      )}
      <PnlDrivers data={data} group={withBudget ? selected : selected === 'start' || selected === 'end' ? 'revenue' : selected} month={month} period={period} base={base} periodLabel={periodLabel} fmt={fmt} notes={notes} />
      <PnlBreakdown kind="cogs" num="04" data={data} month={month} fmt={fmt} notes={notes} />
      <PnlBreakdown kind="investment" num="05" data={data} month={month} fmt={fmt} notes={notes} />
      <PnlTable data={data} month={month} fmt={fmt} notes={notes} />
      {withBudget && <PnlHeatmap data={data} fmt={fmt} notes={notes} />}
    </div>
  );
}
